import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { VerifiedEntry } from '@/types/api';
import InsertDivider from '@/components/suggestions/InsertDivider';
import QuickEntryDialog from '@/components/suggestions/QuickEntryDialog';
import EntryGridCard from './EntryGridCard';

interface EntryGridProps {
  entries: VerifiedEntry[];
  chatId: number;
  libraryId: number;
  onEdit: (entry: VerifiedEntry) => void;
  onViewContext?: (messageId: number) => void;
  onEntryCreated?: (entry: VerifiedEntry) => void;
}

type SortMode = 'chronological' | 'ms_no';

const PAGE_SIZE_OPTIONS = [12, 24, 48, 96];
const DEFAULT_PAGE_SIZE = 24;

const parseNumericMsNo = (value: string | null): number | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
};

const EntryGrid: React.FC<EntryGridProps> = ({
  entries,
  chatId,
  libraryId,
  onEdit,
  onViewContext,
  onEntryCreated,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<SortMode>('chronological');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [insertDialogOpen, setInsertDialogOpen] = useState(false);
  const [insertPosition, setInsertPosition] = useState<{
    beforeId: number;
    afterId: number;
    libraryId: number | null;
  } | null>(null);

  const sortedEntries = useMemo(() => {
    const items = [...entries];

    if (sortBy === 'ms_no') {
      items.sort((a, b) => {
        const aMs = parseNumericMsNo(a.ms_no);
        const bMs = parseNumericMsNo(b.ms_no);

        if (aMs !== null && bMs !== null) {
          if (aMs !== bMs) {
            return aMs - bMs;
          }
          return a.title_message_id - b.title_message_id;
        }

        if (aMs !== null) return -1;
        if (bMs !== null) return 1;
        return a.title_message_id - b.title_message_id;
      });

      return items;
    }

    items.sort((a, b) => a.title_message_id - b.title_message_id);
    return items;
  }, [entries, sortBy]);

  const totalPages = Math.ceil(sortedEntries.length / pageSize);

  const paginatedEntries = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return sortedEntries.slice(startIndex, startIndex + pageSize);
  }, [sortedEntries, page, pageSize]);

  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages]);

  const handleToggleSelect = (entryId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(1);
  };

  const handleSortChange = (event: SelectChangeEvent<SortMode>) => {
    setSortBy(event.target.value as SortMode);
    setPage(1);
  };

  const inferLibraryId = (beforeId: number, afterId: number): number | null => {
    const beforeEntry = sortedEntries.find((entry) => entry.title_message_id === beforeId);
    const afterEntry = sortedEntries.find((entry) => entry.title_message_id === afterId);
    return beforeEntry?.library_id || afterEntry?.library_id || libraryId || null;
  };

  const handleOpenInsertDialog = (beforeId: number, afterId: number) => {
    const inferredLibrary = inferLibraryId(beforeId, afterId);
    setInsertPosition({ beforeId, afterId, libraryId: inferredLibrary });
    setInsertDialogOpen(true);
  };

  const handleCloseInsertDialog = () => {
    setInsertDialogOpen(false);
    setInsertPosition(null);
  };

  const handleEntryCreated = (entry: VerifiedEntry) => {
    setInsertDialogOpen(false);
    setInsertPosition(null);
    onEntryCreated?.(entry);
  };

  if (sortedEntries.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No entries yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Entries added to this library will appear here.
        </Typography>
      </Paper>
    );
  }

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, sortedEntries.length);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{sortedEntries.length} entries</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {startItem}-{endItem} of {sortedEntries.length}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="entry-grid-sort-label">Sort by</InputLabel>
            <Select
              labelId="entry-grid-sort-label"
              value={sortBy}
              label="Sort by"
              onChange={handleSortChange}
            >
              <MenuItem value="chronological">Chronological</MenuItem>
              <MenuItem value="ms_no">MS No (numeric)</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel id="entry-grid-page-size-label">Per page</InputLabel>
            <Select
              labelId="entry-grid-page-size-label"
              value={pageSize}
              label="Per page"
              onChange={handlePageSizeChange}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
        {paginatedEntries.map((entry, index) => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            key={entry.id}
            sx={{ display: 'flex', position: 'relative' }}
          >
            <EntryGridCard
              entry={entry}
              chatId={chatId}
              selected={selectedIds.has(entry.id)}
              onToggleSelect={handleToggleSelect}
              onEdit={onEdit}
              onViewContext={onViewContext}
            />

            {index < paginatedEntries.length - 1 && (
              <InsertDivider
                key={`divider-${entry.title_message_id}-${paginatedEntries[index + 1].title_message_id}`}
                beforeMessageId={entry.title_message_id}
                afterMessageId={paginatedEntries[index + 1].title_message_id}
                onInsert={handleOpenInsertDialog}
              />
            )}
          </Grid>
        ))}
      </Grid>

      {insertPosition && (
        <QuickEntryDialog
          open={insertDialogOpen}
          chatId={chatId}
          libraryId={insertPosition.libraryId}
          beforeMessageId={insertPosition.beforeId}
          afterMessageId={insertPosition.afterId}
          onClose={handleCloseInsertDialog}
          onCreated={handleEntryCreated}
        />
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default EntryGrid;
